import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import { CircularProgress } from "@mui/material";
import {
  StyledFormControl,
  StyledTextField,
} from "../../../../../components/StyledComponents/Inputs/Inputs.js";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../../../../components/StyledComponents/Buttons/AuradineButtons.js";
import { H8TitleMedium } from "../../../../../components/StyledComponents/Typography/Typography.tsx";
import { usePostSaveList } from "../../../../queries/usePostSaveList.ts";
import useAppContextProvider from "../../../../../AppContext/useAppContextProvider.js";
import { usePostDeleteList } from "../../../../queries/usePostDeleteSavedList.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import { useRefetch } from "../../../../MinerPage.js";
import { is } from "date-fns/locale";
import useMinerPageContext from "../../../../context/useMinerPageContext.js";
import { useTheme } from "../../../../../ThemeContext/ThemeContext.js";

// Create a broadcast channel for communicating saved list updates across tabs.
const channel =
  typeof window !== "undefined"
    ? new BroadcastChannel("saved-list-updates")
    : null;

const SavedListForm = ({
  type,
  placeholder,
  setFormOpen,
  refetchFilterNames,
  selectedList,
  setListItemButtonSelectedIndex,
  getValue,
  setFilterValue,
  setSelectedListName,
}) => {
  const { mutate: saveFilterList, isPending: isSaving } =
    usePostSaveList();
  const { colors } = useTheme();
  const { groupFilters, setGroupFilters } =
    useMinerPageContext();
  const {
    mutate: deleteFilterList,
    isPending: isDeleting,
  } = usePostDeleteList();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();

  const typeMapping = {
    Chassis: "chassisListName",
    CBSN: "CBSNListName",
    MAC: "macListName",
  };

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    mode: "all",
    defaultValues: {
      listName: "",
      listContent: "",
    },
  });

  const isEditing =
    selectedList && Object.keys(selectedList).length > 0;

  const { minerPageRefetch } = useRefetch();

  useEffect(() => {
    if (isEditing) {
      const keys = Object.keys(selectedList)[0];
      const values = Object.values(selectedList)[0];
      const valuesString = Array.isArray(values)
        ? values.join("\n")
        : "";
      reset({
        listName: keys,
        listContent: valuesString,
      });
    } else {
      reset({
        listName: "",
        listContent: "",
      });
    }
  }, [selectedList, reset, isEditing]);

  const onSubmit = (data) => {
    if (!type) return;
    const finalListContent = data?.listContent
      ? data?.listContent
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
      : [];
    const finalListName = data.listName.trim();
    saveFilterList(
      {
        filterType: type,
        filterListName: finalListName,
        filterList: finalListContent,
      },
      {
        onSuccess: async (response) => {
          if (response.status === "ok") {
            // Notify other tabs that the list of filters has changed.
            if (channel) {
              channel.postMessage({ type: "update" });
            }
            await refetchFilterNames();

            setToastMessage("List saved successfully.");
            setToastOpen(true);
            reset({
              listName: "",
              listContent: "",
            });
            setFormOpen(false);
            setListItemButtonSelectedIndex(null);
            setSelectedListName("");
            const filterKey = typeMapping[type];
            // Trigger a refetch for the main miner table to ensure the data reflects
            // the updated content of the currently applied saved list filter.
            if (
              isEditing &&
              groupFilters[filterKey] === finalListName
            ) {
              await minerPageRefetch();
            }
          } else {
            setToastMessage(
              response.msg || "Error saving list"
            );
            setToastOpen(true);
          }
        },
        onError: async (error) => {
          setToastMessage(
            error.message || "Error saving list"
          );
          setToastOpen(true);
        },
      }
    );
  };

  const handleDelete = () => {
    if (!type || !isEditing) return;
    const listName = getValues("listName");
    deleteFilterList(
      {
        filterType: type,
        filterListName: listName,
      },
      {
        onSuccess: async (response) => {
          if (response.status === "ok") {
            // Notify other tabs that the list of filters has changed.
            if (channel) {
              channel.postMessage({ type: "update" });
            }
            await refetchFilterNames();

            const filterKey = typeMapping[type];
            // If the deleted list was the one actively filtering the table...
            if (groupFilters[filterKey] === listName) {
              // ...update the central filter state. This will trigger a re-render
              // and URL update in the parent component.
              const newGroupFilters = { ...groupFilters };
              delete newGroupFilters[filterKey];
              setGroupFilters(newGroupFilters);
            }

            setListItemButtonSelectedIndex(null);
            reset({
              listName: "",
              listContent: "",
            });
            setSelectedListName("");
            setToastMessage("List deleted successfully.");
            setToastOpen(true);
            setFormOpen(false);
          } else {
            setToastMessage(
              response.msg || "Error deleting list."
            );
            setToastOpen(true);
          }
        },
        onError: (error) => {
          setToastMessage(
            error.message || "Error deleting list."
          );
          setToastOpen(true);
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" flexDirection="column" gap={2}>
        {/* List Name */}
        <Box display="flex" flexDirection="column" gap={1}>
          <H8TitleMedium>Name</H8TitleMedium>
          <Controller
            name="listName"
            control={control}
            rules={{
              required: "Please enter a list name.",
              pattern: {
                value: /^[A-Za-z0-9_-]+$/,
                message:
                  "Letters, numbers, dash and underscore only.",
              },
              maxLength: {
                value: 32,
                message: "Up to 32 characters allowed.",
              },
            }}
            render={({ field }) => (
              <StyledTextField colors={colors}
                {...field}
                label="List Name"
                variant="outlined"
                error={!!errors.listName}
                helperText={errors.listName?.message}
                fullWidth
                disabled={isEditing}
              />
            )}
          />
        </Box>

        {/* List Content */}
        <Box display="flex" flexDirection="column" gap={1}>
          <H8TitleMedium>Entries</H8TitleMedium>
          <Controller
            name="listContent"
            control={control}
            rules={{
              required: "Please enter at least one item.",
              validate: (value) => {
                const lines = value
                  .split("\n")
                  .map((l) => l.trim())
                  .filter(Boolean);
                if (lines.length === 0) {
                  return "At least one line is required.";
                }
                if (lines.length > 1000) {
                  return "Maximum 1000 lines allowed.";
                }
                if (type === "CBSN" || type === "Chassis") {
                  for (let line of lines) {
                    if (!/^[A-Za-z0-9]+$/.test(line)) {
                      return "Only letters & numbers per line.";
                    }
                  }
                } else if (type === "MAC") {
                  for (let line of lines) {
                    if (
                      !/^(?:[0-9A-Fa-f]{2}([-:]))(?:[0-9A-Fa-f]{2}\1){4}[0-9A-Fa-f]{2}$|^(?:[0-9A-Fa-f]{4}\.){2}[0-9A-Fa-f]{4}$/.test(
                        line.trim()
                      )
                    ) {
                      return "Invalid MAC address format.";
                    }
                  }
                }
                return true;
              },
            }}
            render={({ field }) => (
                <StyledTextField colors={colors}
                  {...field}
                  label="Items (one per line, max 1000 lines)"
                  placeholder={placeholder}
                  variant="outlined"
                  multiline
                  minRows={10}
                  maxRows={10}
                  error={!!errors.listContent}
                  helperText={errors.listContent?.message}
                  fullWidth
                />
            )}
          />
        </Box>

        {/* Submit */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pt={1}
          className="action-container"
        >
          {isEditing ? (
            <SecondaryButton colors={colors}
              onClick={handleDelete}
              disabled={isSaving || isDeleting}
              startIcon={<DeleteIcon />}
              endIcon={
                isDeleting ? (
                  <CircularProgress size={20} sx={{ color: colors.primary.main }} />
                ) : null
              }
            >
              {isDeleting ? "Deleting..." : "Delete list"}
            </SecondaryButton>
          ) : (
            <div />
          )}
          <PrimaryButton colors={colors}
            type="submit"
            disabled={!isValid || isSaving || isDeleting}
            endIcon={
              isSaving ? (
                <CircularProgress size={20} sx={{ color: colors.primary.main }} />
              ) : null
            }
          >
            {isSaving ? "Saving..." : "Save List"}
          </PrimaryButton>
        </Box>
      </Box>
    </form>
  );
};

export default SavedListForm;
